/**
 * Rep Performance Intelligence
 * 
 * Opportunity-aware scoring that evaluates rep performance
 * relative to the quality of leads they receive.
 * No leaderboards. No over-ranking. Fair and contextual.
 */

import type { RepScorecard } from "@/data/commissionEngine";
import { SALES_REPS, type RepProfile } from "@/lib/sourceIntelligence";

/* ═══ Types ═══ */

export type PerformanceSignal = "strong_on_quality" | "underperforming_on_quality" | "consistent" | "volume_limited" | "high_efficiency";

export interface RepPerformanceInsight {
  repName: string;
  region: string;
  // Core metrics
  netConversion: number;         // % — sold / total leads assigned
  highFitConversion: number;     // % — sold on high-fit (75+) leads
  revenuePerLead: number;        // $
  // Opportunity context
  avgLeadQuality: "High" | "Medium" | "Low";
  totalLeadsAssigned: number;
  highFitLeads: number;
  // Insight
  signal: PerformanceSignal;
  insight: string;
  insightSeverity: "positive" | "neutral" | "caution";
}

/* ═══ Seed data — simulates lead-level fit data per rep ═══ */
interface RepLeadProfile {
  name: string;
  totalLeads: number;
  highFitLeads: number;      // leads with fit score >= 75
  soldFromHighFit: number;
  soldFromAll: number;
  totalRevenue: number;
}

const REP_LEAD_PROFILES: RepLeadProfile[] = [
  { name: "Wesley Woodfin", totalLeads: 42, highFitLeads: 28, soldFromHighFit: 16, soldFromAll: 19, totalRevenue: 142000 },
  { name: "Kyle Schall",    totalLeads: 38, highFitLeads: 22, soldFromHighFit: 14, soldFromAll: 17, totalRevenue: 128000 },
  { name: "Lucas Schall",   totalLeads: 30, highFitLeads: 12, soldFromHighFit: 5,  soldFromAll: 8,  totalRevenue: 64000 },
  { name: "Trent Hill",     totalLeads: 25, highFitLeads: 18, soldFromHighFit: 10, soldFromAll: 11, totalRevenue: 88000 },
];

/* ═══ Engine ═══ */

function deriveSignal(
  netConv: number,
  highFitConv: number,
  totalLeads: number,
): { signal: PerformanceSignal; insight: string; severity: "positive" | "neutral" | "caution" } {
  if (totalLeads < 10) {
    return {
      signal: "volume_limited",
      insight: "Limited data — not enough leads to evaluate performance patterns",
      severity: "neutral",
    };
  }
  if (highFitConv >= 55 && netConv >= 40) {
    return {
      signal: "strong_on_quality",
      insight: "Strong performance on high-quality leads — efficient closer",
      severity: "positive",
    };
  }
  if (highFitConv < 40 && netConv >= 30) {
    return {
      signal: "underperforming_on_quality",
      insight: "Underperforming on high-fit opportunities — review approach on qualified leads",
      severity: "caution",
    };
  }
  if (netConv >= 35 && highFitConv >= 45) {
    return {
      signal: "high_efficiency",
      insight: "High efficiency — converting well across lead quality levels",
      severity: "positive",
    };
  }
  if (Math.abs(highFitConv - netConv) <= 10) {
    return {
      signal: "consistent",
      insight: "Consistent conversion across lead types — stable performer",
      severity: "neutral",
    };
  }
  return {
    signal: "consistent",
    insight: "Performance within expected range — no anomalies detected",
    severity: "neutral",
  };
}

export function computeRepPerformanceInsights(): RepPerformanceInsight[] {
  return REP_LEAD_PROFILES.map(profile => {
    const repProfile = SALES_REPS.find(r => r.name === profile.name);
    const netConversion = profile.totalLeads > 0
      ? Math.round((profile.soldFromAll / profile.totalLeads) * 100) : 0;
    const highFitConversion = profile.highFitLeads > 0
      ? Math.round((profile.soldFromHighFit / profile.highFitLeads) * 100) : 0;
    const revenuePerLead = profile.totalLeads > 0
      ? Math.round(profile.totalRevenue / profile.totalLeads) : 0;
    const avgQualityPct = profile.totalLeads > 0
      ? Math.round((profile.highFitLeads / profile.totalLeads) * 100) : 0;
    const avgLeadQuality: "High" | "Medium" | "Low" =
      avgQualityPct >= 60 ? "High" : avgQualityPct >= 40 ? "Medium" : "Low";

    const { signal, insight, severity } = deriveSignal(netConversion, highFitConversion, profile.totalLeads);

    return {
      repName: profile.name,
      region: repProfile?.region || "Unknown",
      netConversion,
      highFitConversion,
      revenuePerLead,
      avgLeadQuality,
      totalLeadsAssigned: profile.totalLeads,
      highFitLeads: profile.highFitLeads,
      signal,
      insight,
      insightSeverity: severity,
    };
  });
}

/** Get insight for a specific rep */
export function getRepInsight(repName: string): RepPerformanceInsight | undefined {
  return computeRepPerformanceInsights().find(r => r.repName === repName);
}
